import type { CertificateTemplateOption } from '~/gql/certificates/certificates'
import { Copy, Plus, Star, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog from '~/components/ConfirmDialog'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Label } from '~/components/ui/label'
import { tourAnchor } from '~/helpers/tour/anchors'
import NewTemplateForm from './NewTemplateForm'

/** Les modèles et ce qu'on peut en faire ; les actions disent si elles ont abouti. */
export type TemplateActions = {
  templates: CertificateTemplateOption[]
  selected: CertificateTemplateOption | null
  select: (id: string) => void
  create: (name: string) => Promise<boolean>
  duplicate: (id: string, name: string) => Promise<boolean>
  makeDefault: (id: string) => Promise<boolean>
  remove: (id: string) => Promise<boolean>
}

type Props = { templates: TemplateActions }

/**
 * Les modèles d'attestation de l'organisation (#162) : choisir celui à éditer,
 * en créer, en dupliquer, désigner le défaut, en supprimer — jamais le défaut,
 * repli de tous les parcours qui n'en choisissent pas.
 */
function CertificateTemplatesBar({ templates }: Props) {
  const { t } = useTranslation()
  const [creating, setCreating] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const { selected } = templates

  return (
    <Card {...tourAnchor('certificates.templates')}>
      <CardHeader>
        <CardTitle>{t('certificates.templates.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex min-w-56 flex-1 flex-col gap-1.5">
            <Label htmlFor="certTemplate">{t('certificates.templates.current')}</Label>
            <select
              id="certTemplate"
              value={selected?.id ?? ''}
              onChange={e => templates.select(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            >
              {templates.templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.isDefault ? t('certificates.templates.defaultOption', { name: template.name }) : template.name}
                </option>
              ))}
            </select>
          </div>
          <Button type="button" variant="outline" onClick={() => setCreating(true)}>
            <Plus className="mr-2 size-4" />
            {t('certificates.templates.new')}
          </Button>
          {selected && (
            <>
              <Button type="button" variant="outline" onClick={() => void templates.duplicate(selected.id, t('certificates.templates.copyName', { name: selected.name }))}>
                <Copy className="mr-2 size-4" />
                {t('certificates.templates.duplicate')}
              </Button>
              <Button type="button" variant="outline" disabled={selected.isDefault} onClick={() => void templates.makeDefault(selected.id)}>
                <Star className="mr-2 size-4" />
                {t('certificates.templates.makeDefault')}
              </Button>
              <Button type="button" variant="outline" disabled={selected.isDefault} onClick={() => setConfirmingDelete(true)}>
                <Trash2 className="mr-2 size-4" />
                {t('common.delete')}
              </Button>
            </>
          )}
        </div>
        {selected?.isDefault && <p className="text-xs text-muted-foreground">{t('certificates.templates.defaultHelp')}</p>}
        {creating && <NewTemplateForm onCreate={templates.create} onCancel={() => setCreating(false)} />}
      </CardContent>
      {selected && (
        <ConfirmDialog
          open={confirmingDelete}
          title={t('certificates.templates.deleteTitle', { name: selected.name })}
          confirmLabel={t('common.delete')}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => {
            setConfirmingDelete(false)
            void templates.remove(selected.id)
          }}
        >
          {t('certificates.templates.deleteBody')}
        </ConfirmDialog>
      )}
    </Card>
  )
}

export default CertificateTemplatesBar
